import { z } from 'zod/v4';

const normalizedEmail = (message = 'Некорректный формат email') =>
  z
    .string()
    .email(message)
    .transform((e) => e.toLowerCase().trim());

// ==================== Login Schemas ====================

export const adminLoginSchema = z.object({
  email: normalizedEmail(),
  password: z.string().min(6, 'Пароль должен быть не менее 6 символов'),
  /**
   * Подсказка предпочитаемого тенанта (localStorage). Если админ найден в этой
   * базе — она становится активной; иначе берётся первая по порядку реестра.
   */
  preferredTenant: z.string().min(1).optional(),
});

export type AdminLoginDto = z.infer<typeof adminLoginSchema>;

export const adminChangePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Текущий пароль обязателен'),
  newPassword: z.string().min(6, 'Новый пароль должен быть не менее 6 символов'),
});

export type AdminChangePasswordDto = z.infer<typeof adminChangePasswordSchema>;

// ==================== Invite Schemas ====================

export const adminInviteSchema = z.object({
  email: normalizedEmail(),
  username: z.string().min(3, 'Имя пользователя должно быть не менее 3 символов'),
  roleId: z.number().optional(),
  lang: z.string().min(2).max(5).optional(),
});

export type AdminInviteDto = z.infer<typeof adminInviteSchema>;

export const adminSetPasswordSchema = z.object({
  token: z.string().min(1, 'Токен обязателен'),
  password: z.string().min(6, 'Пароль должен быть не менее 6 символов'),
});

export type AdminSetPasswordDto = z.infer<typeof adminSetPasswordSchema>;

// ==================== Password Reset Schemas ====================

export const adminForgotPasswordSchema = z.object({
  email: normalizedEmail(),
  lang: z.string().min(2).max(5).optional(),
});

export type AdminForgotPasswordDto = z.infer<typeof adminForgotPasswordSchema>;

export const adminResetPasswordSchema = z.object({
  token: z.string().min(1, 'Токен обязателен'),
  newPassword: z.string().min(6, 'Пароль должен быть не менее 6 символов'),
});

export type AdminResetPasswordDto = z.infer<typeof adminResetPasswordSchema>;

// ==================== Response Types ====================

export interface AdminTokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // секунды до истечения access токена
}

export interface AdminAccessTokenResponse {
  accessToken: string;
  expiresIn: number; // секунды до истечения access токена
}

export interface AdminTenantSummary {
  slug: string;
  name: string;
  logoUrl: string;
}

export interface AdminMeResponse {
  id: number;
  email: string;
  roles: string[];
  permissions: string[];
  createdAt: Date;
  /**
   * Активный тенант (slug). Заполняется из tenant-claim запроса.
   */
  tenant: string;
  /**
   * Тенанты, к которым у админа есть активная запись AdminUser.
   * В однотенантном режиме — один элемент.
   */
  tenants: AdminTenantSummary[];
}

export const adminSwitchTenantSchema = z.object({
  tenant: z.string().min(1, 'Тенант обязателен'),
});

export type AdminSwitchTenantDto = z.infer<typeof adminSwitchTenantSchema>;

export { type JwtPayload } from '@masters-club/types';
